// A dimension on paper (SchematicDim). Paper mm, Y down. What it READS follows
// AutoCAD's trans-spatial rule: when both ends sit inside one viewport, the
// figure is the model distance behind them (paper distance / the viewport's
// scale); otherwise it is the paper distance. The line itself is placed by the
// model's own resolver, `dimensionGeometry`, so a paper dimension and a model
// one behave alike (drag out to pick horizontal / vertical / aligned).
// Pure and DOM-free (unit-tested).

import type {
  SchematicDim,
  SheetViewport,
  Vec2,
} from '../shared/types';
import {
  dimensionGeometry,
  type DimGeom,
} from './geometry';
import { viewportAtPaper } from './sheets';

/** Where the dimension line sits, and what it reads (mm) and in which space. */
export function paperDimFigure(d: SchematicDim, viewports: readonly SheetViewport[]): { geom: DimGeom; mm: number; model: boolean } | null {
  const geom = dimensionGeometry(d.a, d.b, d.offset, d.kind ?? 'aligned');
  if (!geom) return null;
  const va = viewportAtPaper(viewports, d.a), vb = viewportAtPaper(viewports, d.b);
  const through = va && va === vb && va.scale > 0 ? va : null;
  return { geom, mm: through ? geom.length / through.scale : geom.length, model: !!through };
}

/** The signed offset that puts the dimension line through `p`. */
export function paperDimOffset(a: Vec2, b: Vec2, p: Vec2, kind: SchematicDim['kind'] = 'aligned'): number {
  const g = dimensionGeometry(a, b, 0, kind);
  if (!g) return 0;
  const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  return (p.x - mid.x) * g.n.x + (p.y - mid.y) * g.n.y;
}
